/** What a person confirms about a bill at review: its scope and category, its vendor, its period -
 * ADR-0212 decisions 3 and 4.
 *
 * Each row is a chip with its reason behind an info control. **Every one of them is a suggestion**
 * (SEC-05): nothing is linked or recorded by this panel except the vendor, and that only when the
 * person presses Confirm. Accepting the bill is the existing act on the screen and the only route
 * into a figure (`AWAITING_REVIEW` is the only way into `VERIFIED`).
 *
 * - **Scope and Category** are the classification preview for the chosen site: advice from the same
 *   function Accept calls, so it cannot say something Accept then contradicts. It needs a site (the
 *   site's jurisdiction decides the factor set) and, for a fuel invoice, the person's answer about
 *   how the fuel was used. If classification **refuses** (an unsupported fuel), the refusal names
 *   what is supported and **no chip is pre-filled**: nothing is substituted.
 * - **Vendor** is shown as *Suggested: X* only when the stated supplier equals a vendor's name after
 *   trimming case and spacing; a near miss is no suggestion. The person confirms the suggestion,
 *   picks another, or (an Admin or Approver) adds the stated name as a new vendor.
 * - **Period** is what the document states, shown so it is confirmed rather than assumed.
 */

import { useState } from "react";

import type { ReactNode } from "react";

import type { DocumentRow } from "../../api/evidence";
import {
  useClassificationPreview,
  useSetDocumentVendor,
  useVendorSuggestion,
} from "../../api/processing";
import { useRecordVendor, useRegisters } from "../../api/sources";
import { ReadFailed, WriteFailed } from "../../components/Loaded";
import { InfoPopover, StatusChip } from "../../components/ui";
import { categoryLabel } from "../../lib/categoryLabel";
import { shortDate } from "../../lib/format";
import { ReasonPopover } from "./ReasonPopover";
import styles from "./ReviewConfirm.module.css";

export interface ReviewConfirmProps {
  document: DocumentRow;
  facilityId: string;
  fuelUse: string;
  /** Whether this person may accept (and so may be shown the classification). */
  mayClassify: boolean;
  /** Whether this person may link a vendor (the roles that upload). */
  mayVendor: boolean;
  /** Whether this person may add a vendor to the register (Admin and Approver). */
  mayAddVendor: boolean;
}

function Row({ label, info, children }: { label: string; info?: ReactNode; children: ReactNode }) {
  return (
    <li className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{children}</span>
      <span className={styles.info}>{info}</span>
    </li>
  );
}

function ScopeRows({ document, facilityId, fuelUse }: Pick<ReviewConfirmProps, "document" | "facilityId" | "fuelUse">) {
  const isFuel = document.kind === "fuel_invoice";
  // A fuel invoice is classified only once the person has said how the fuel was used: asking
  // earlier would be a refusal the screen predicted.
  const ready = facilityId !== "" && (!isFuel || fuelUse !== "");
  const preview = useClassificationPreview(document.id, facilityId, fuelUse, ready);

  if (!ready) {
    return (
      <>
        <Row label="Scope" info={<InfoPopover infoKey="processing.review-scope" />}>
          <span className={styles.none} data-testid="classification-waiting">
            {facilityId === "" ? "Choose a site" : "Say how the fuel was used"}
          </span>
        </Row>
        <Row label="Category">
          <span className={styles.none}>–</span>
        </Row>
      </>
    );
  }
  if (preview.error) {
    return (
      <Row label="Scope">
        <ReadFailed of="The scope and category" error={preview.error} onRetry={() => void preview.refetch()} />
      </Row>
    );
  }
  if (preview.isPending || !preview.data) {
    return (
      <Row label="Scope">
        <span className={styles.none} role="status">
          Working it out
        </span>
      </Row>
    );
  }
  const found = preview.data;
  if (found.outcome === "refused") {
    return (
      <Row label="Scope" info={<ReasonPopover title="Why no scope" reasons={[found.refusal ?? "Not classified.", ...supported(found)]} />}>
        <span data-testid="classification-refused">
          <StatusChip tone="crit" size="sm">
            Cannot be classified
          </StatusChip>
        </span>
      </Row>
    );
  }
  return (
    <>
      <Row label="Scope" info={<ReasonPopover title="Why this scope" reasons={found.because ?? []} />}>
        <span data-testid="classification-scope" data-scope={found.scope ?? ""}>
          <StatusChip tone="info" size="sm">
            {found.scope ? `Scope ${found.scope}` : "Scope unknown"}
          </StatusChip>
        </span>
      </Row>
      <Row label="Category" info={<InfoPopover infoKey="processing.review-scope" />}>
        <span data-testid="classification-category" data-category={found.category ?? ""}>
          <StatusChip tone="info" size="sm">
            {found.category ? categoryLabel(found.category) : "Not classified"}
          </StatusChip>
        </span>
      </Row>
    </>
  );
}

function supported(found: { supported_fuels?: string[]; supported_refrigerants?: string[] }): string[] {
  const lines: string[] = [];
  const fuels = found.supported_fuels ?? [];
  const refrigerants = found.supported_refrigerants ?? [];
  if (fuels.length) lines.push(`Supported fuels here: ${fuels.join(", ")}.`);
  if (refrigerants.length) lines.push(`Supported refrigerants here: ${refrigerants.join(", ")}.`);
  return lines;
}

/** The register's vendors, read only when the person opens the picker: a read nobody asked for is a
 * failure nobody can see, and the list is not needed to show a suggestion. A failed read is the
 * shared failure with a retry, never an empty select that reads as "you keep no vendors". */
function VendorChoice({
  busy,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  onConfirm: (vendorId: string) => void;
  onCancel: () => void;
}) {
  const registers = useRegisters();
  const [pick, setPick] = useState("");
  return (
    <span className={styles.form}>
      {registers.error ? (
        <ReadFailed of="The list of vendors" error={registers.error} onRetry={() => void registers.refetch()} />
      ) : (
        <select aria-label="Choose a vendor" value={pick} onChange={(event) => setPick(event.target.value)}>
          <option value="">Choose a vendor…</option>
          {(registers.data?.vendors ?? []).map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.name}
            </option>
          ))}
        </select>
      )}
      <button className={styles.confirm} type="button" disabled={!pick || busy} onClick={() => onConfirm(pick)}>
        Confirm vendor
      </button>
      <button className={styles.link} type="button" onClick={onCancel}>
        Cancel
      </button>
    </span>
  );
}

function VendorRow({
  document,
  mayVendor,
  mayAddVendor,
}: Pick<ReviewConfirmProps, "document" | "mayVendor" | "mayAddVendor">) {
  const suggestion = useVendorSuggestion(document.id);
  const link = useSetDocumentVendor(document.id);
  const addVendor = useRecordVendor();
  const [choosing, setChoosing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const info = <InfoPopover infoKey="processing.review-vendor" />;
  if (suggestion.error) {
    return (
      <Row label="Vendor" info={info}>
        <ReadFailed of="The vendor" error={suggestion.error} onRetry={() => void suggestion.refetch()} />
      </Row>
    );
  }
  if (suggestion.isPending || !suggestion.data) {
    return (
      <Row label="Vendor" info={info}>
        <span className={styles.none} role="status">
          Looking up
        </span>
      </Row>
    );
  }
  const { current, suggested, read_as: readAs } = suggestion.data;

  async function confirm(vendorId: string | null) {
    setRefusal(null);
    try {
      await link.mutateAsync({ vendorId, expectedVersion: document.version });
      setChoosing(false);
      setAdding(false);
    } catch (error) {
      setRefusal(error);
    }
  }

  async function addAndConfirm() {
    setRefusal(null);
    try {
      const made = await addVendor.mutateAsync({ name: newName.trim() });
      await link.mutateAsync({ vendorId: made.id, expectedVersion: document.version });
      setAdding(false);
      setNewName("");
    } catch (error) {
      setRefusal(error);
    }
  }

  const busy = link.isPending || addVendor.isPending;

  return (
    <Row label="Vendor" info={info}>
      <span className={styles.vendor} data-testid="review-vendor">
        {current ? (
          <>
            <span data-testid="vendor-confirmed" data-vendor-id={current.id}>
              <StatusChip tone="ok" size="sm">
                {current.name}
              </StatusChip>
            </span>
            {mayVendor ? (
              <>
                <button className={styles.link} type="button" onClick={() => setChoosing(true)}>
                  Choose another
                </button>
                <button className={styles.link} type="button" disabled={busy} onClick={() => void confirm(null)}>
                  Clear
                </button>
              </>
            ) : null}
          </>
        ) : suggested ? (
          <>
            <span data-testid="vendor-suggested" data-vendor-id={suggested.id}>
              <StatusChip tone="warn" size="sm">
                Suggested: {suggested.name}
              </StatusChip>
            </span>
            {mayVendor ? (
              <>
                <button
                  className={styles.confirm}
                  type="button"
                  disabled={busy}
                  onClick={() => void confirm(suggested.id)}
                >
                  Confirm vendor
                </button>
                <button className={styles.link} type="button" onClick={() => setChoosing(true)}>
                  Choose another
                </button>
              </>
            ) : null}
          </>
        ) : (
          <>
            <span data-testid="vendor-none">
              <StatusChip tone="neutral" size="sm">
                {readAs ? `Stated: ${readAs}` : "Not stated"}
              </StatusChip>
            </span>
            {mayVendor ? (
              <button className={styles.link} type="button" onClick={() => setChoosing(true)}>
                Choose a vendor
              </button>
            ) : null}
          </>
        )}
        {mayVendor && mayAddVendor && !current ? (
          <button
            className={styles.link}
            type="button"
            onClick={() => {
              setAdding(true);
              setNewName(readAs ?? "");
            }}
          >
            Add as new vendor
          </button>
        ) : null}
      </span>

      {choosing ? (
        <VendorChoice
          busy={busy}
          onConfirm={(vendorId) => void confirm(vendorId)}
          onCancel={() => setChoosing(false)}
        />
      ) : null}

      {adding ? (
        <span className={styles.form}>
          <input
            aria-label="New vendor name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <button
            className={styles.confirm}
            type="button"
            disabled={!newName.trim() || busy}
            onClick={() => void addAndConfirm()}
          >
            Add and confirm
          </button>
          <button className={styles.link} type="button" onClick={() => setAdding(false)}>
            Cancel
          </button>
        </span>
      ) : null}
      <WriteFailed error={refusal} of="the vendor" onReload={() => void suggestion.refetch()} />
    </Row>
  );
}

export function ReviewConfirm(props: ReviewConfirmProps) {
  const { document } = props;
  return (
    <ul className={styles.rows} aria-label="Confirm what this bill is" data-testid="review-confirm">
      {props.mayClassify ? (
        <ScopeRows document={document} facilityId={props.facilityId} fuelUse={props.fuelUse} />
      ) : null}
      {props.mayVendor ? <VendorRow document={document} mayVendor={props.mayVendor} mayAddVendor={props.mayAddVendor} /> : null}
      <Row label="Period">
        {document.period_start && document.period_end ? (
          <span data-testid="review-period">
            {shortDate(document.period_start)} &ndash; {shortDate(document.period_end)}
          </span>
        ) : (
          <span data-testid="review-period">
            <StatusChip tone="warn" size="sm">
              Not established
            </StatusChip>
          </span>
        )}
      </Row>
    </ul>
  );
}
